"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

const STORAGE_KEY = "vektr.calculatorPos";
const HEADER_HEIGHT = 64;
const TOP_GAP = 20;
const RIGHT_GAP = 20;
// Calculator is rendered at 448x and scaled 0.8 — these constants are the
// *visual* dimensions used for clamping and default-position math.
export const CALC_BASE_WIDTH = 448;
export const CALC_SCALE = 0.8;
export const CALC_WIDTH = Math.round(CALC_BASE_WIDTH * CALC_SCALE); // 358
export const HANDLE_BASE_HEIGHT = 32;
export const HANDLE_HEIGHT = Math.round(HANDLE_BASE_HEIGHT * CALC_SCALE); // 26

interface Pos {
  x: number;
  y: number;
}

// Which face the calculator window shows: the real calculator, or the
// multiplication-table lookup. Lifted to context so the overlay can drop the
// "locked" tint while the (always-available) table is showing.
export type CalculatorView = "calc" | "tabell";

interface CalculatorContextValue {
  open: boolean;
  pos: Pos;
  openCalc: () => void;
  closeCalc: () => void;
  toggleCalc: () => void;
  setPos: (p: Pos) => void;
  // True when calculator currently captures arrow keys (mouse over or
  // mid-drag). Other pages read this to suppress their own arrow-key
  // shortcuts so navigation doesn't fight the calculator's cursor.
  keyboardActive: boolean;
  setKeyboardActive: (v: boolean) => void;
  view: CalculatorView;
  setView: (v: CalculatorView) => void;
}

const Ctx = createContext<CalculatorContextValue | null>(null);

function defaultPos(): Pos {
  if (typeof window === "undefined") {
    return { x: 0, y: HEADER_HEIGHT + TOP_GAP };
  }
  return {
    x: Math.max(0, window.innerWidth - CALC_WIDTH - RIGHT_GAP),
    y: HEADER_HEIGHT + TOP_GAP,
  };
}

function readStoredPos(): Pos | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== "number" || typeof parsed?.y !== "number") {
      return null;
    }
    return { x: parsed.x, y: parsed.y };
  } catch {
    return null;
  }
}

function clampPos(p: Pos): Pos {
  if (typeof window === "undefined") return p;
  const maxX = Math.max(0, window.innerWidth - CALC_WIDTH);
  const maxY = Math.max(0, window.innerHeight - HANDLE_HEIGHT);
  return {
    x: Math.min(Math.max(0, p.x), maxX),
    y: Math.min(Math.max(0, p.y), maxY),
  };
}

export function CalculatorProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  // SSR-safe initial state. Real position is hydrated from localStorage (or
  // computed against window) in the mount effect below — Overlay is always
  // display:none until user clicks, so the brief mismatch is invisible.
  const [pos, setPosState] = useState<Pos>({
    x: 0,
    y: HEADER_HEIGHT + TOP_GAP,
  });

  useEffect(() => {
    const stored = readStoredPos();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPosState(stored ? clampPos(stored) : defaultPos());
  }, []);

  useEffect(() => {
    function onResize() {
      setPosState((prev) => clampPos(prev));
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const setPos = useCallback((p: Pos) => {
    const clamped = clampPos(p);
    setPosState(clamped);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(clamped));
    } catch {
      // localStorage disabled — position persists in-memory only this session
    }
  }, []);

  const openCalc = useCallback(() => setOpen(true), []);
  const closeCalc = useCallback(() => setOpen(false), []);
  const toggleCalc = useCallback(() => setOpen((o) => !o), []);

  const [keyboardActive, setKeyboardActive] = useState(false);
  const [view, setView] = useState<CalculatorView>("calc");

  return (
    <Ctx.Provider
      value={{
        open,
        pos,
        openCalc,
        closeCalc,
        toggleCalc,
        setPos,
        keyboardActive,
        setKeyboardActive,
        view,
        setView,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useCalculator(): CalculatorContextValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error("useCalculator must be used inside CalculatorProvider");
  }
  return v;
}
